import React, { createContext, useContext, useReducer, useCallback, useMemo } from 'react';
import { sanitizeSearchQuery } from '../utils/security';
import { useGlobalSearch } from '../hooks/useGlobalSearch';

const SearchContext = createContext();

// Search action types
const SEARCH_ACTIONS = {
  SET_SEARCH_QUERY: 'SET_SEARCH_QUERY',
  SET_SHOW_SEARCH_RESULTS: 'SET_SHOW_SEARCH_RESULTS',
  SET_FILTER_CATEGORY: 'SET_FILTER_CATEGORY',
  CLEAR_SEARCH: 'CLEAR_SEARCH',
  RESET_ALL_SEARCH: 'RESET_ALL_SEARCH',
};

// Initial search state
const initialSearchState = {
  searchQuery: '',
  showSearchResults: false,
  filterCategory: 'all',
};

// Search reducer
const searchReducer = (state, action) => {
  switch (action.type) {
    case SEARCH_ACTIONS.SET_SEARCH_QUERY:
      return {
        ...state,
        searchQuery: action.payload,
      };

    case SEARCH_ACTIONS.SET_SHOW_SEARCH_RESULTS:
      return {
        ...state,
        showSearchResults: action.payload,
      };

    case SEARCH_ACTIONS.SET_FILTER_CATEGORY:
      return {
        ...state,
        filterCategory: action.payload,
      };

    case SEARCH_ACTIONS.CLEAR_SEARCH:
      return {
        ...state,
        searchQuery: '',
        showSearchResults: false,
      };

    case SEARCH_ACTIONS.RESET_ALL_SEARCH:
      return { ...initialSearchState };

    default:
      return state;
  }
};

export const SearchProvider = React.memo(({ children, supabase }) => {
  const [searchState, dispatch] = useReducer(searchReducer, initialSearchState);

  // Global search hook
  const globalSearch = useGlobalSearch(supabase);

  // Search actions
  const setSearchQuery = useCallback(
    (query, field) => {
      dispatch({ type: SEARCH_ACTIONS.SET_SEARCH_QUERY, payload: query });
      const sanitizedQuery = sanitizeSearchQuery(query);
      globalSearch.search(sanitizedQuery, field);
    },
    [globalSearch]
  );

  const setShowSearchResults = useCallback(show => {
    dispatch({ type: SEARCH_ACTIONS.SET_SHOW_SEARCH_RESULTS, payload: show });
  }, []);

  const setFilterCategory = useCallback(
    category => {
      dispatch({ type: SEARCH_ACTIONS.SET_FILTER_CATEGORY, payload: category });
      // Re-run the current query with the new field filter
      if (searchState.searchQuery) {
        const sanitizedQuery = sanitizeSearchQuery(searchState.searchQuery);
        globalSearch.search(sanitizedQuery, category);
      }
    },
    [globalSearch, searchState.searchQuery]
  );

  const clearSearch = useCallback(() => {
    dispatch({ type: SEARCH_ACTIONS.CLEAR_SEARCH });
    dispatch({ type: SEARCH_ACTIONS.SET_FILTER_CATEGORY, payload: 'all' });
    globalSearch.clearSearch();
  }, [globalSearch]);

  const resetAllSearch = useCallback(() => {
    dispatch({ type: SEARCH_ACTIONS.RESET_ALL_SEARCH });
    globalSearch.clearSearch();
  }, [globalSearch]);

  // Search filtering logic
  const getFilteredItems = useCallback((items, searchQuery) => {
    if (!searchQuery || !items) return [];

    const query = searchQuery.toLowerCase();
    return items.filter(item => {
      return (
        item.title?.toLowerCase().includes(query) ||
        item.studio?.toLowerCase().includes(query) ||
        item.year?.toString().includes(query) ||
        item.director?.toLowerCase().includes(query) ||
        item.actors?.toLowerCase().includes(query) ||
        item.genre?.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query) ||
        item.language?.toLowerCase().includes(query)
      );
    });
  }, []);

  // Memoized filtered search results
  const getFilteredSearchItems = useCallback(
    masterItems => {
      return getFilteredItems(masterItems, searchState.searchQuery);
    },
    [searchState.searchQuery, getFilteredItems]
  );

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      // State
      ...searchState,

      // Global search results from server
      globalSearchResults: globalSearch.searchResults,
      isGlobalSearching: globalSearch.isSearching,
      hasGlobalResults: globalSearch.hasResults,

      // Actions
      setSearchQuery,
      setShowSearchResults,
      setFilterCategory,
      clearSearch,
      resetAllSearch,

      // Utility functions (for client-side filtering when needed)
      getFilteredItems,
      getFilteredSearchItems,
    }),
    [
      searchState,
      globalSearch.searchResults,
      globalSearch.isSearching,
      globalSearch.hasResults,
      setSearchQuery,
      setShowSearchResults,
      setFilterCategory,
      clearSearch,
      resetAllSearch,
      getFilteredItems,
      getFilteredSearchItems,
    ]
  );

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
});

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};

// Export search action types and initial state for convenience
export { SEARCH_ACTIONS, initialSearchState };
